/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { HomeIcon, PlusIcon, Text, UnstyledButton } from '@/shared/ui';
import { Menu } from '@/shared/ui/components/basic/Menu';
import { DealStageBadge } from '@/features/deals/components/DealStageBadge';
import { cn } from '@/shared/ui/utils/tailwind';
import type { ClientRow } from '../context/ClientManagementContext';
import { useClientOpenAttendance } from '../hooks/useClientOpenAttendance';

/** Filled action, 38px tall to sit level with the header's other controls. */
export const CLIENT_PRIMARY_ACTION_CLASS =
  'flex h-[38px] items-center gap-(--space-2) whitespace-nowrap rounded-(--radius-md) border-none bg-(--button-primary-bg) px-(--space-4) text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] text-(--button-primary-text) cursor-pointer hover:bg-(--button-primary-hover-bg)';

/** Bordered ghost action: the page ground shows through it by design. */
export const CLIENT_SECONDARY_ACTION_CLASS =
  'flex h-[38px] items-center gap-(--space-2) whitespace-nowrap rounded-(--radius-md) border border-solid border-(--border-medium) bg-(--surface-primary) px-(--space-4) text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary) cursor-pointer hover:bg-(--surface-hover)';

export interface AttendanceActionsProps {
  client: ClientRow;
  agencyId: string;
  onCreateDeal: (client: ClientRow) => void;
  /** Opens the given attendance's detail page. */
  onViewAttendance?: (dealId: string) => void;
}

/**
 * The client header's attendance actions, driven by whether the client
 * already has an open one. While the lookup is in flight nothing renders,
 * so the primary label never flashes from one meaning to the other.
 *
 * @param props - The client, agency, and the create/view callbacks.
 * @returns The action buttons, or null while the lookup loads.
 */
export function AttendanceActions({
  client,
  agencyId,
  onCreateDeal,
  onViewAttendance,
}: AttendanceActionsProps) {
  const { t } = useTranslation('features/clients');
  const { isLoading, openAttendances } = useClientOpenAttendance(agencyId, client.client_id);

  if (isLoading) return null;

  if (openAttendances.length === 0) {
    return (
      <UnstyledButton
        data-testid="attendance-primary-action"
        onClick={() => onCreateDeal(client)}
        className={CLIENT_PRIMARY_ACTION_CLASS}
      >
        <PlusIcon width={14} height={14} />
        {t('profile.startAttendance')}
      </UnstyledButton>
    );
  }

  return (
    <>
      {openAttendances.length === 1 ? (
        <UnstyledButton
          data-testid="attendance-primary-action"
          onClick={() => onViewAttendance?.(openAttendances[0].deal_id)}
          className={CLIENT_PRIMARY_ACTION_CLASS}
        >
          {t('profile.viewAttendance', { count: 1 })}
        </UnstyledButton>
      ) : (
        <Menu position="bottom-end" width={320}>
          <Menu.Target>
            <UnstyledButton
              data-testid="attendance-primary-action"
              className={CLIENT_PRIMARY_ACTION_CLASS}
            >
              {t('profile.viewAttendance', { count: openAttendances.length })}
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown className="p-0">
            {openAttendances.map((deal, index) => (
              <Menu.Item
                key={deal.deal_id}
                data-testid="attendance-option"
                onClick={() => onViewAttendance?.(deal.deal_id)}
                className={cn(
                  'items-start gap-(--space-2-5) rounded-none px-(--space-3) py-(--space-2-5)',
                  index < openAttendances.length - 1 && 'border-b border-(--border-subtle)',
                )}
              >
                <HomeIcon
                  width={14}
                  height={14}
                  className="mt-(--space-0-5) shrink-0 text-(--text-tertiary)"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-(--space-1)">
                  <Text
                    component="span"
                    className="text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)] text-(--text-primary)"
                  >
                    {deal.property_title || deal.client_name || t('profile.selectAttendance')}
                  </Text>
                  <Text component="span" className="self-start">
                    <DealStageBadge stage={deal.stage} />
                  </Text>
                </div>
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
      <UnstyledButton
        data-testid="attendance-secondary-action"
        onClick={() => onCreateDeal(client)}
        className={CLIENT_SECONDARY_ACTION_CLASS}
      >
        <PlusIcon width={14} height={14} />
        {t('profile.startNewAttendance')}
      </UnstyledButton>
    </>
  );
}
